/**
 * Ignores clicks that land too soon after a set of buttons appears.
 *
 * A double-tap is two taps at one spot. When the first tap swaps the screen,
 * the second lands on whatever the new screen put there — and on the typed
 * check-in that can be "Yes, that's me" under a name in the pick list, or the
 * first class of an ambiguous result under "Yes" (the client choosing the
 * class, which D-009 forbids). A click whose timestamp falls inside the
 * settle window after mount is dropped; a deliberate tap a moment later works.
 *
 * Why a time window rather than "the pointerdown must follow the mount": the
 * second tap of a double-tap *does* start after the new card mounts. What
 * gives it away is only how soon.
 *
 * `settleMs` of 0 (or less) accepts everything, from the first frame — so a
 * shared component can take the guard as an opt-in without changing behaviour
 * for anyone who doesn't ask for it.
 */

import { useCallback, useEffect, useRef } from "react";

export function useSettledClick(settleMs: number) {
  // Infinity until the mount effect runs: with a guard, nothing is accepted
  // before the buttons have actually been committed to the screen.
  const shownAtRef = useRef(Number.POSITIVE_INFINITY);

  useEffect(() => {
    shownAtRef.current = performance.now();
  }, []);

  // Event timestamps and performance.now() share the page's time origin.
  return useCallback(
    (event: { timeStamp: number }) =>
      settleMs <= 0 || event.timeStamp - shownAtRef.current >= settleMs,
    [settleMs],
  );
}
